import { ChecklistState } from "../state/state";
import { initialState } from "../state/initialState";
import { STORAGE_KEY } from "../global_settings";

// Interfaces and constants

/**
 * Creating an interface for dependecy injection
 * If mvoing from localStorage to a DB, this could be extended
 */
export interface StorageProvider {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

/**
 * Data within the StoargeProvider's storage
 */
const data = new Map<string, string>();

/**
 * The storage for the StorageProvider
 */
const storage: StorageProvider = {
    getItem(key) {
        return data.get(key) ?? null;
    },

    setItem(key, value) {
        data.set(key, value);
    }
};

/**
 * Interface to capture various detals about the StorageProvider
 * storage: which StorageProvider to use
 */
interface StorageOptions {
    storage?: StorageProvider;
}

/**
 * Factory for creating a StorageProvider
 * @returns StorageProvider
 */
export function createMemoryStorage(): StorageProvider {
    const data = new Map<string, string>();

    return {
        getItem(key) {
            return data.get(key) ?? null;
        },

        setItem(key, value) {
            data.set(key, value);
        }
    };
}

// Functions

/**
 * Handles the migration of data to the ChecklistState type
 * @param state is an (outdated or possibly malformed) object from (local) storage
 * @returns state that is of CheckListState
 */
export function migrateState(state: unknown): ChecklistState {
    if (!state || typeof state !== "object") {
        return structuredClone(initialState);
    }

    const migrated = structuredClone(initialState);

    for (const [sectionName, initialSection] of Object.entries(initialState)) {
        const savedSection = (state as Record<string, unknown>)[sectionName];

        if (
            savedSection &&
            typeof savedSection === "object" &&
            "items" in savedSection
        ) {
            migrated[sectionName].items = {
                ...initialSection.items,
                ...(savedSection as { items: typeof initialSection.items }).items
            };
        }
    }

    return migrated;
}

/**
 * Checks if an object is a ChecklistState
 * @param value
 * @returns boolean
 */
export function isChecklistState(value: unknown): value is ChecklistState {
  // if empty or not an `object` => FALSE
  if (!value || typeof value !== "object") {
      return false;
  }

  return Object.values(value).every(section => {
    // if any section is empty or not an `object` => FALSE
    if (!section || typeof section !== "object") {
      return false;
    }
    // if items not found in section => FALSE
    if (!("items" in section)) {
      return false;
    }
    // if any item is empty or not an `object` => FALSE
    if (!section.items || typeof section.items !== "object") {
        return false;
    }
    // if any id is empty or not an `object` => FALSE
    // also if item.checked && item.priority are not the right type => FAlSE
    return Object.values(section.items).every(item =>
        typeof item === "object" &&
        item !== null &&
        "checked" in item &&
        typeof item.checked === "boolean" &&
        "priority" in item &&
        typeof item.priority === "string"
    );
    });
}

/**
 * Stave only valid state under the key to the
 * stroage defined in the StorageProvider
 * Default to window.localStorage
 * @param storageKey
 * @param state
 * @param storageOptions
 */
export function saveState(
    state: unknown,
    storageKey: string = STORAGE_KEY,
    storageOptions: StorageOptions = {}
): void {
  if (!isChecklistState(state)) {
        throw new Error("Invalid checklist state");
    }
  const storage = storageOptions.storage ?? window.localStorage;
  storage.setItem(
    storageKey,
    JSON.stringify(state)
  );
}

/**
 * Load state stored under storageKey
 * @param storageKey
 * @param storageOptions
 * @returns
 */
export function loadState(
    storageKey: string = STORAGE_KEY,
    storageOptions: StorageOptions = {}
): ChecklistState {
    const storage = storageOptions.storage ?? window.localStorage;
    const stored = storage.getItem(storageKey);
    const initial = structuredClone(initialState);

    if (stored === null) {
        storage.setItem(storageKey, JSON.stringify(initial));
        return initial;
    }

    try {
        return migrateState(JSON.parse(stored));
    } catch {
        storage.setItem(storageKey, JSON.stringify(initial));
        return initial;
    }
}
